
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import axios from 'axios'
import Pagination from '@/components/Pagination/index.vue'
import UploadExcel from '@/components/UploadExcel/index.vue'
import PinList, { PinListItem } from '@/components/ota/PinList.vue'
import PinForm from '@/components/ota/PinForm.vue'
import { getUserOtaInfo, clearUserOtaOverride } from '@/api/userOtaOverride'
import { listWarrantyPeriods, WarrantyPeriod } from '@/api/warrantyPeriods'
import { listFeatures, Feature } from '@/api/entitlements'
import { getDeviceFeatures, addDeviceFeature, removeDeviceFeature, removeDeviceFeatureById, DeviceFeature } from '@/api/deviceFeatures'
import { startRepair, completeRepair, processRefund, bulkRegisterWithSku, BulkDeviceWithSku, syncMissingFeaturesScan, syncMissingFeaturesApply } from '@/api/devices'
import { createSku, addFeatureToSku } from '@/api/skus'

interface Device {
  id?: number
  serialNumber: string
  productType: string
  purchaseType: string
  currentUserId?: string
  lifecycleStatus: string
  purchaseDate?: Date
  activationDate?: Date
  warrantyEndDate?: Date
  warrantyPeriodId?: number
  zohoOrderId?: string
  notes?: string
  metadataJson?: any
  createdAt?: Date
  updatedAt?: Date
  warrantyPeriod?: WarrantyPeriod
}

interface DeviceFeatureForm {
  featureKey: string
  durationType: 'permanent' | 'days' | 'months' | 'years'
  durationValue: number
}

@Component({
  name: 'DeviceManagement',
  components: {
    Pagination,
    UploadExcel,
    PinList,
    PinForm
  }
})
export default class extends Vue {
  // Pin Yönet drawer state — single-device scope. Opened from the Actions
  // column. PinForm runs in device-scope mode (lockedDeviceSerial), so the
  // user-scope toggle is hidden and the OTA list is filtered to the cane's
  // major automatically by the form.
  private pinDrawerVisible = false
  private pinDrawerDevice: any = null
  private pinDrawerLoading = false
  // Pins on this specific device (filtered from the user's full pin list).
  private pinDrawerPins: PinListItem[] = []
  // Full user pin list — PinForm uses this for the device-scope "existingPin"
  // computed lookup so updating an existing pin shows "Pin'i güncelle".
  private pinDrawerAllUserPins: PinListItem[] = []
  // Cane list for PinForm; device-scope locks selectedSerial but the form
  // still expects a userDevices array to render the locked-state hint.
  private pinDrawerUserDevices: any[] = []
  private pinDrawerAvailableOtaFiles: any[] = []
  private pinRemovingKey: string | null = null

  // Same pin state but scoped to the Device Details dialog. Kept separate
  // from the drawer state so the two surfaces don't fight when both are
  // (briefly) open during the close animation.
  private viewPinLoading = false
  private viewPinList: PinListItem[] = []
  private viewAllUserPins: PinListItem[] = []
  private viewUserDevices: any[] = []
  private viewAvailableOtaFiles: any[] = []
  private viewPinRemovingKey: string | null = null

  private list: Device[] = []
  private listLoading = true
  private total = 0
  private listQuery = {
    page: 1,
    limit: 20,
    serialNumber: '',
    productType: '',
    lifecycleStatus: '',
    user: ''
  }

  // Sync Missing Features
  private syncLoading = false
  private syncDialogVisible = false
  private syncResult: any = null
  private syncApplying = false

  private dialogFormVisible = false
  private dialogConnectVisible = false
  private dialogDisconnectVisible = false
  private dialogLifecycleVisible = false
  private dialogReplaceVisible = false
  private dialogViewVisible = false
  private dialogHistoryVisible = false
  private dialogBulkVisible = false
  private dialogStatus = ''

  private formData: any = {
    serialNumber: '',
    productType: 'V2',
    purchaseType: 'REGULAR',
    purchaseDate: null,
    warrantyPeriodId: null,
    warrantyEndDate: null,
    zohoOrderId: '',
    notes: '',
    features: [] as DeviceFeatureForm[]
  }

  private newFeature: DeviceFeatureForm = {
    featureKey: '',
    durationType: 'years',
    durationValue: 1
  }

  private connectData = {
    serialNumber: '',
    userId: ''
  }

  private disconnectData = {
    serialNumber: '',
    currentUserId: '',
    reason: ''
  }

  private lifecycleData = {
    serialNumber: '',
    status: '',
    reason: ''
  }

  private dialogPurchaseTypeVisible = false
  private purchaseTypeData = {
    serialNumber: '',
    purchaseType: '',
    reason: ''
  }

  private replaceData = {
    oldSerialNumber: '',
    newSerialNumber: '',
    newProductType: 'V2',
    reason: ''
  }

  private replaceSearchLoading = false
  private replaceSearchResults: Device[] = []

  private viewData: Device = {
    serialNumber: '',
    productType: '',
    purchaseType: '',
    lifecycleStatus: ''
  }

  private historyData: any[] = []

  // Bulk Import with SKU
  private bulkImportStep = 0
  private excelData: BulkDeviceWithSku[] = []
  private bulkValidating = false
  private bulkRegistering = false
  private missingSkus: Array<{ skuCode: string, count: number, created: boolean }> = []
  private validatedSkuCount = 0
  private uniqueDeviceCount = 0
  private registeredCount = 0
  private registrationProgress = 0
  private registrationResult: any = null

  // Create SKU Dialog
  private dialogCreateSkuVisible = false
  private currentMissingSku: any = null
  private newSkuForm = {
    skuCode: '',
    name: '',
    description: '',
    productType: 'V2',
    isActive: true,
    features: [] as Array<{ featureId: number, durationType: string, durationValue: number }>
  }

  private newSkuFeature = {
    featureId: null as number | null,
    durationType: 'years',
    durationValue: 1
  }

  private skuRules = {
    skuCode: [{ required: true, message: 'SKU Code is required', trigger: 'blur' }],
    name: [{ required: true, message: 'Name is required', trigger: 'blur' }],
    productType: [{ required: true, message: 'Product Type is required', trigger: 'change' }]
  }

  // Repair & Refund dialogs
  private dialogRepairStartVisible = false
  private dialogRepairCompleteVisible = false
  private dialogRefundVisible = false

  private repairData = {
    serialNumber: '',
    currentUserId: '',
    reason: ''
  }

  private repairCompleteData = {
    serialNumber: '',
    repairStartDate: '',
    previousUserId: '',
    repairDays: 0,
    notes: ''
  }

  private refundData = {
    serialNumber: '',
    currentUserId: '',
    reason: ''
  }

  // Warranty periods and features
  private warrantyPeriods: WarrantyPeriod[] = []
  private featuresList: Feature[] = []
  private deviceFeatures: DeviceFeature[] = []
  private dialogFeaturesVisible = false
  private viewDeviceSerialNumber = ''
  private loadingFeatures = false

  private rules = {
    serialNumber: [{ required: true, message: 'Serial number is required', trigger: 'blur' }],
    productType: [{ required: true, message: 'Product type is required', trigger: 'change' }],
    purchaseType: [{ required: true, message: 'Purchase type is required', trigger: 'change' }]
  }

  created() {
    this.getList()
    this.loadWarrantyPeriods()
    this.loadFeatures()
  }

  private async loadWarrantyPeriods() {
    try {
      const { data } = await listWarrantyPeriods()
      this.warrantyPeriods = data.data || []
    } catch (error) {
      console.error('Failed to load warranty periods:', error)
    }
  }

  private async loadFeatures() {
    try {
      const { data } = await listFeatures()
      this.featuresList = data.data || []
    } catch (error) {
      console.error('Failed to load features:', error)
    }
  }

  private async getList() {
    this.listLoading = true
    try {
      const params: any = {}
      if (this.listQuery.serialNumber) params.serialNumber = this.listQuery.serialNumber
      if (this.listQuery.productType) params.productType = this.listQuery.productType
      if (this.listQuery.lifecycleStatus) params.lifecycleStatus = this.listQuery.lifecycleStatus
      if (this.listQuery.user) params.user = this.listQuery.user.trim()
      params.limit = this.listQuery.limit
      params.offset = (this.listQuery.page - 1) * this.listQuery.limit

      const response = await axios.get(`${process.env.VUE_APP_BASE_API}/devices/search`, { params })
      if (response.data.success) {
        this.list = response.data.data
        this.total = response.data.total || response.data.count || this.list.length
      }
    } catch (error) {
      this.$message.error('Failed to load devices')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.getList()
  }

  private resetFormData() {
    this.formData = {
      serialNumber: '',
      productType: 'V2',
      purchaseType: 'REGULAR',
      purchaseDate: null,
      warrantyPeriodId: null,
      warrantyEndDate: null,
      zohoOrderId: '',
      notes: '',
      features: [] as DeviceFeatureForm[]
    }
    this.newFeature = {
      featureKey: '',
      durationType: 'years',
      durationValue: 1
    }
  }

  private addFeatureToForm() {
    if (!this.newFeature.featureKey) {
      this.$message.warning('Please select a feature')
      return
    }
    // Check if feature already added
    if (this.formData.features.some((f: DeviceFeatureForm) => f.featureKey === this.newFeature.featureKey)) {
      this.$message.warning('Feature already added')
      return
    }
    this.formData.features.push({ ...this.newFeature })
    this.newFeature = {
      featureKey: '',
      durationType: 'years',
      durationValue: 1
    }
  }

  private removeFeatureFromForm(index: number) {
    this.formData.features.splice(index, 1)
  }

  private getFeatureName(featureKey: string): string {
    const feature = this.featuresList.find(f => f.featureKey === featureKey)
    return feature ? feature.name : featureKey
  }

  private formatDuration(durationType: string, durationValue: number): string {
    if (durationType === 'permanent') return 'Permanent'
    return `${durationValue} ${durationType}`
  }

  private handleCreate() {
    this.resetFormData()
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as ElForm).clearValidate()
    })
  }

  private async saveDevice() {
    (this.$refs.dataForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          const data: any = {
            serialNumber: this.formData.serialNumber,
            productType: this.formData.productType,
            purchaseType: this.formData.purchaseType,
            purchaseDate: this.formData.purchaseDate,
            zohoOrderId: this.formData.zohoOrderId,
            notes: this.formData.notes
          }

          // Add warranty period if selected
          if (this.formData.warrantyPeriodId) {
            data.warrantyPeriodId = this.formData.warrantyPeriodId
          } else if (this.formData.warrantyEndDate) {
            // Manual warranty end date
            data.warrantyEndDate = this.formData.warrantyEndDate
          }

          // Add features if any
          if (this.formData.features && this.formData.features.length > 0) {
            data.features = this.formData.features
          }

          await axios.post(`${process.env.VUE_APP_BASE_API}/devices/register`, data)
          this.$message.success('Device registered successfully')
          this.dialogFormVisible = false
          this.getList()
        } catch (error: any) {
          this.$message.error(error.response?.data?.error || 'Registration failed')
          console.error(error)
        }
      }
    })
  }

  private handleConnect(row: Device) {
    this.connectData = {
      serialNumber: row.serialNumber,
      userId: ''
    }
    this.dialogConnectVisible = true
  }

  private async confirmConnect() {
    if (!this.connectData.userId) {
      this.$message.warning('Please enter User ID')
      return
    }

    try {
      await axios.post(
        `${process.env.VUE_APP_BASE_API}/devices/${this.connectData.serialNumber}/connect`,
        { userId: this.connectData.userId }
      )
      this.$message.success('Device connected successfully')
      this.dialogConnectVisible = false
      this.getList()
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Connection failed')
      console.error(error)
    }
  }

  private handleDisconnect(row: Device) {
    this.disconnectData = {
      serialNumber: row.serialNumber,
      currentUserId: row.currentUserId || '',
      reason: ''
    }
    this.dialogDisconnectVisible = true
  }

  private async confirmDisconnect() {
    try {
      await axios.post(
        `${process.env.VUE_APP_BASE_API}/devices/${this.disconnectData.serialNumber}/disconnect`,
        {
          force: true, // Admin force disconnect - bypasses user check
          reason: this.disconnectData.reason
        }
      )
      this.$message.success('Device disconnected successfully')
      this.dialogDisconnectVisible = false
      this.getList()
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Disconnection failed')
      console.error(error)
    }
  }

  private async handleView(row: Device) {
    this.viewData = { ...row }
    this.viewDeviceSerialNumber = row.serialNumber
    // Parallel fetch — features and pin info don't depend on each other and
    // the dialog opens once both resolve. Without parallel, the dialog has
    // a visible "popping in" of the pin section.
    await Promise.all([
      this.loadDeviceFeatures(row.serialNumber),
      this.loadPinDataForView(row)
    ])
    this.dialogViewVisible = true
  }

  // Mirror of showPinManager's data fetch but targets the view dialog's state.
  // Skips entirely when the device has no current user — backend would 400 on
  // an empty user id and the form wouldn't render anyway.
  private async loadPinDataForView(row: Device) {
    if (!row.currentUserId) {
      this.viewPinList = []
      this.viewAllUserPins = []
      this.viewUserDevices = []
      this.viewAvailableOtaFiles = []
      return
    }
    this.viewPinLoading = true
    try {
      const { data } = await getUserOtaInfo(row.currentUserId)
      const payload = data.data || {}
      this.viewUserDevices = payload.devices || []
      const allPins: PinListItem[] = (payload.overrides || []).map((o: any) => ({
        key: o.deviceSerialNumber || 'user-scope',
        deviceSerial: o.deviceSerialNumber,
        otaFileId: o.otaFileId,
        appVersion: o.otaFile?.applicationVersionName || '',
        wirelessVersion: o.otaFile?.wirelessVersionName || null,
        reason: o.reason,
        setBy: o.setBy,
        createdAt: o.createdAt
      }))
      this.viewAllUserPins = allPins
      // Same filter the drawer uses: this device's own pin plus any user-scope
      // pin (which also applies here when the major matches).
      this.viewPinList = allPins.filter(
        p => p.deviceSerial === row.serialNumber || p.deviceSerial === null
      )
      this.viewAvailableOtaFiles = (payload.activeOtaFiles || []).map((f: any) => ({
        id: f.id,
        application_version_name: f.applicationVersionName,
        wireless_version_name: f.wirelessVersionName,
        is_mandatory: f.isMandatory
      }))
    } catch (err: any) {
      this.$message.error(this.$t('devicesScreen.loadPinInfoFailed', { msg: err.message || '' }) as string)
    } finally {
      this.viewPinLoading = false
    }
  }

  private async onViewPinSaved() {
    if (this.viewData) await this.loadPinDataForView(this.viewData)
  }

  private async removePinFromView(p: PinListItem) {
    if (!this.viewData?.currentUserId) return
    const isUserScope = p.deviceSerial === null
    const scopeLabel = isUserScope ? this.$t('otaPin.allCanesScope') as string : p.deviceSerial
    try {
      await this.$confirm(
        this.$t('otaPin.removeConfirm', { version: p.appVersion, scope: scopeLabel }) as string,
        this.$t('otaPin.removeTitle') as string,
        {
          confirmButtonText: this.$t('otaPin.removeConfirmButton') as string,
          cancelButtonText: this.$t('table.cancel') as string
        }
      )
    } catch {
      return
    }
    this.viewPinRemovingKey = p.key
    try {
      await clearUserOtaOverride(
        this.viewData.currentUserId,
        isUserScope ? undefined : (p.deviceSerial || undefined),
        isUserScope
      )
      this.$message.success(this.$t('otaPin.removed') as string)
      await this.loadPinDataForView(this.viewData)
    } catch (err: any) {
      this.$message.error(this.$t('otaPin.removeFailed', { msg: err.message || '' }) as string)
    } finally {
      this.viewPinRemovingKey = null
    }
  }

  private async loadDeviceFeatures(serialNumber: string) {
    this.loadingFeatures = true
    try {
      const { data } = await getDeviceFeatures(serialNumber, true)
      this.deviceFeatures = data.data || []
    } catch (error) {
      console.error('Failed to load device features:', error)
      this.deviceFeatures = []
    } finally {
      this.loadingFeatures = false
    }
  }

  private async handleManageFeatures(row: Device) {
    this.viewDeviceSerialNumber = row.serialNumber
    await this.loadDeviceFeatures(row.serialNumber)
    this.dialogFeaturesVisible = true
  }

  private async handleAddFeatureToDevice() {
    if (!this.newFeature.featureKey) {
      this.$message.warning('Please select a feature')
      return
    }
    try {
      await addDeviceFeature(this.viewDeviceSerialNumber, {
        featureKey: this.newFeature.featureKey,
        durationType: this.newFeature.durationType,
        durationValue: this.newFeature.durationType !== 'permanent' ? this.newFeature.durationValue : undefined,
        sourceType: 'admin_grant'
      })
      this.$message.success('Feature added successfully')
      this.newFeature = { featureKey: '', durationType: 'years', durationValue: 1 }
      await this.loadDeviceFeatures(this.viewDeviceSerialNumber)
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Failed to add feature')
    }
  }

  private async handleRemoveDeviceFeature(feature: DeviceFeature) {
    try {
      await this.$confirm('Are you sure you want to remove this feature?', 'Confirm', {
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
      // Target the exact row — multiple rows per (device, feature) are
      // allowed so removing by featureKey alone is ambiguous.
      await removeDeviceFeatureById(this.viewDeviceSerialNumber, feature.id)
      this.$message.success('Feature removed successfully')
      await this.loadDeviceFeatures(this.viewDeviceSerialNumber)
    } catch (error: any) {
      if (error !== 'cancel') {
        this.$message.error(error.response?.data?.error || 'Failed to remove feature')
      }
    }
  }

  private getFeatureStatus(feature: DeviceFeature): string {
    if (!feature.isActive) return 'Inactive'
    if (feature.durationType === 'permanent') return 'Active (Permanent)'
    if (feature.endDate) {
      const endDate = new Date(feature.endDate)
      if (endDate < new Date()) return 'Expired'
      const days = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      return `Active (${days} days left)`
    }
    return 'Active'
  }

  private getFeatureStatusType(feature: DeviceFeature): string {
    if (!feature.isActive) return 'info'
    if (feature.durationType === 'permanent') return 'success'
    if (feature.endDate) {
      const endDate = new Date(feature.endDate)
      if (endDate < new Date()) return 'danger'
      const days = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      if (days <= 30) return 'warning'
    }
    return 'success'
  }

  private async handleLifecycleAction(command: string, row: Device) {
    if (command === 'connect') {
      await this.handleConnect(row)
    } else if (command === 'disconnect') {
      await this.handleDisconnect(row)
    } else if (command === 'features') {
      await this.handleManageFeatures(row)
    } else if (command === 'history') {
      await this.loadDeviceHistory(row)
    } else if (command === 'lifecycle') {
      this.lifecycleData = {
        serialNumber: row.serialNumber,
        status: row.lifecycleStatus,
        reason: ''
      }
      this.dialogLifecycleVisible = true
    } else if (command === 'purchaseType') {
      this.purchaseTypeData = {
        serialNumber: row.serialNumber,
        purchaseType: row.purchaseType || '',
        reason: ''
      }
      this.dialogPurchaseTypeVisible = true
    } else if (command === 'replace') {
      this.replaceData = {
        oldSerialNumber: row.serialNumber,
        newSerialNumber: '',
        newProductType: row.productType,
        reason: ''
      }
      this.replaceSearchResults = []
      this.dialogReplaceVisible = true
    } else if (command === 'startRepair') {
      this.repairData = {
        serialNumber: row.serialNumber,
        currentUserId: row.currentUserId || '',
        reason: ''
      }
      this.dialogRepairStartVisible = true
    } else if (command === 'completeRepair') {
      const repairMetadata = row.metadataJson?.repair
      this.repairCompleteData = {
        serialNumber: row.serialNumber,
        repairStartDate: repairMetadata?.startDate || '',
        previousUserId: repairMetadata?.previousUserId || '',
        repairDays: 0,
        notes: ''
      }
      this.dialogRepairCompleteVisible = true
    } else if (command === 'refund') {
      this.refundData = {
        serialNumber: row.serialNumber,
        currentUserId: row.currentUserId || '',
        reason: ''
      }
      this.dialogRefundVisible = true
    }
  }

  private async loadDeviceHistory(row: Device) {
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_BASE_API}/devices/${row.serialNumber}/history`
      )
      if (response.data.success) {
        this.historyData = response.data.data
        this.dialogHistoryVisible = true
      }
    } catch (error) {
      this.$message.error('Failed to load device history')
      console.error(error)
    }
  }

  private async confirmLifecycleUpdate() {
    if (!this.lifecycleData.status) {
      this.$message.warning('Please select a status')
      return
    }

    try {
      await axios.put(
        `${process.env.VUE_APP_BASE_API}/devices/${this.lifecycleData.serialNumber}/lifecycle`,
        {
          status: this.lifecycleData.status,
          reason: this.lifecycleData.reason
        }
      )
      this.$message.success('Device status updated successfully')
      this.dialogLifecycleVisible = false
      this.getList()
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Update failed')
      console.error(error)
    }
  }

  private async confirmPurchaseTypeUpdate() {
    if (!this.purchaseTypeData.purchaseType) {
      this.$message.warning('Please select a purchase type')
      return
    }

    try {
      await axios.put(
        `${process.env.VUE_APP_BASE_API}/devices/${this.purchaseTypeData.serialNumber}/purchase-type`,
        {
          purchaseType: this.purchaseTypeData.purchaseType,
          reason: this.purchaseTypeData.reason
        }
      )
      this.$message.success('Purchase type updated')
      this.dialogPurchaseTypeVisible = false
      this.getList()
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Update failed')
      console.error(error)
    }
  }

  private async searchReplacementDevices(query: string) {
    if (!query || query.length < 2) {
      this.replaceSearchResults = []
      return
    }

    this.replaceSearchLoading = true
    try {
      const response = await axios.get(`${process.env.VUE_APP_BASE_API}/devices/search`, {
        params: { serialNumber: query, limit: 20 }
      })

      if (response.data.success) {
        // Filter out the old device itself
        this.replaceSearchResults = (response.data.data || []).filter(
          (d: Device) => d.serialNumber !== this.replaceData.oldSerialNumber
        )
      }
    } catch (error) {
      console.error(error)
      this.replaceSearchResults = []
    } finally {
      this.replaceSearchLoading = false
    }
  }

  private onReplacementDeviceSelected(serialNumber: string) {
    const selected = this.replaceSearchResults.find(d => d.serialNumber === serialNumber)
    if (selected) {
      this.replaceData.newProductType = selected.productType
    }
  }

  private async confirmReplace() {
    if (!this.replaceData.newSerialNumber) {
      this.$message.warning('Please select a replacement device')
      return
    }

    try {
      await axios.post(
        `${process.env.VUE_APP_BASE_API}/devices/${this.replaceData.oldSerialNumber}/replace`,
        {
          newSerialNumber: this.replaceData.newSerialNumber,
          newProductType: this.replaceData.newProductType,
          reason: this.replaceData.reason
        }
      )
      this.$message.success('Device replaced successfully')
      this.dialogReplaceVisible = false
      this.getList()
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Replacement failed')
      console.error(error)
    }
  }

  private async handleSyncMissingFeatures(zohoPage = 1) {
    // The scan reads every package page from Zoho (one API call per package)
    // and can take minutes; it was started once by accident, so ask first.
    try {
      await this.$confirm(
        this.$t('devicesScreen.syncConfirm', { page: zohoPage }) as string,
        'Sync Missing Features',
        {
          confirmButtonText: this.$t('devicesScreen.startScan') as string,
          cancelButtonText: this.$t('table.cancel') as string,
          type: 'warning'
        }
      )
    } catch {
      return
    }
    this.syncLoading = true
    this.syncResult = null
    this.syncDialogVisible = true
    try {
      const res = await syncMissingFeaturesScan(zohoPage)
      this.syncResult = res.data
    } catch (err: any) {
      this.$message.error('Failed to scan devices: ' + (err.message || 'Unknown error'))
      this.syncDialogVisible = false
    } finally {
      this.syncLoading = false
    }
  }

  private async applySyncFeatures() {
    if (!this.syncResult || !this.syncResult.details) return
    this.syncApplying = true
    try {
      const serials = this.syncResult.details
        .filter((d: any) => d.status === 'would_update')
        .map((d: any) => d.serial)
      const res = await syncMissingFeaturesApply(serials)
      this.syncResult = res.data
      this.$message.success(`Features applied to ${res.data.updated} devices`)
    } catch (err: any) {
      this.$message.error('Failed to apply features: ' + (err.message || 'Unknown error'))
    } finally {
      this.syncApplying = false
    }
  }

  private handleBulkRegister() {
    // Reset all bulk import state
    this.bulkImportStep = 0
    this.excelData = []
    this.missingSkus = []
    this.validatedSkuCount = 0
    this.uniqueDeviceCount = 0
    this.registeredCount = 0
    this.registrationProgress = 0
    this.registrationResult = null
    this.bulkValidating = false
    this.bulkRegistering = false
    this.dialogBulkVisible = true
  }

  // ============ Excel Upload Methods ============

  private beforeExcelUpload(file: File) {
    const isValidSize = file.size / 1024 / 1024 < 5 // 5MB limit
    if (!isValidSize) {
      this.$message.error('File size must be less than 5MB')
      return false
    }
    return true
  }

  private handleExcelSuccess({ header, results }: { header: string[], results: any[] }) {
    // Map Excel columns to device data
    // Required columns: Serial Number (or serialNumber), SKU (or skuCode)
    // Optional columns: Date, First Name, Last Name, Email, Phone, Country Code, Country
    const serialNumberCol = header.find(h =>
      h.toLowerCase().includes('serial') || h.toLowerCase() === 'serialnumber'
    ) || header[0]

    const skuCol = header.find(h =>
      h.toLowerCase().includes('sku') || h.toLowerCase() === 'skucode'
    ) || header[1]

    const dateCol = header.find(h =>
      h.toLowerCase().includes('date') || h.toLowerCase() === 'purchasedate'
    )

    // Purchaser information columns
    const firstNameCol = header.find(h =>
      h.toLowerCase().includes('first') && h.toLowerCase().includes('name') ||
      h.toLowerCase() === 'firstname' || h.toLowerCase() === 'first_name'
    )

    const lastNameCol = header.find(h =>
      h.toLowerCase().includes('last') && h.toLowerCase().includes('name') ||
      h.toLowerCase() === 'lastname' || h.toLowerCase() === 'last_name' ||
      h.toLowerCase() === 'surname'
    )

    // Full name column (will be split into first/last name if separate columns don't exist)
    const fullNameCol = header.find(h =>
      (h.toLowerCase() === 'name' || h.toLowerCase() === 'full name' ||
       h.toLowerCase() === 'fullname' || h.toLowerCase() === 'full_name' ||
       h.toLowerCase() === 'customer name' || h.toLowerCase() === 'customer') &&
      !h.toLowerCase().includes('first') && !h.toLowerCase().includes('last')
    )

    const emailCol = header.find(h =>
      h.toLowerCase().includes('email') || h.toLowerCase() === 'e-mail'
    )

    const phoneCol = header.find(h =>
      h.toLowerCase().includes('phone') || h.toLowerCase().includes('tel') ||
      h.toLowerCase() === 'mobile'
    )

    const countryCodeCol = header.find(h =>
      h.toLowerCase().includes('country') && h.toLowerCase().includes('code') ||
      h.toLowerCase() === 'countrycode' || h.toLowerCase() === 'country_code'
    )

    const countryCol = header.find(h =>
      (h.toLowerCase() === 'country' || h.toLowerCase().includes('country')) &&
      !h.toLowerCase().includes('code')
    )

    // Address columns - supports "Address Line", "Adress Line" (typo), "Address"
    const addressCol = header.find(h =>
      h.toLowerCase().includes('address') || h.toLowerCase().includes('adress')
    )

    // City column
    const cityCol = header.find(h =>
      h.toLowerCase() === 'city' || h.toLowerCase().includes('city')
    )

    // Track last serial number for rows without one (same device, multiple SKUs)
    let lastSerialNumber = ''

    this.excelData = results.map((row: any) => {
      let purchaseDate = dateCol ? row[dateCol] : undefined
      // Handle Excel date serial number
      if (typeof purchaseDate === 'number') {
        const excelEpoch = new Date(1899, 11, 30)
        const date = new Date(excelEpoch.getTime() + purchaseDate * 86400000)
        purchaseDate = date.toISOString().split('T')[0]
      } else if (purchaseDate instanceof Date) {
        purchaseDate = purchaseDate.toISOString().split('T')[0]
      }

      // Get serial number from row, or use last one if empty
      let serialNumber = String(row[serialNumberCol] || '').trim()
      if (serialNumber) {
        lastSerialNumber = serialNumber
      } else {
        // Use previous serial number for rows without one (multiple SKUs for same device)
        serialNumber = lastSerialNumber
      }

      // Handle name parsing - either from separate columns or split from full name
      let firstName: string | undefined
      let lastName: string | undefined

      if (firstNameCol && row[firstNameCol]) {
        firstName = String(row[firstNameCol]).trim() || undefined
      }
      if (lastNameCol && row[lastNameCol]) {
        lastName = String(row[lastNameCol]).trim() || undefined
      }

      // If no separate first/last name columns, try to split full name
      if (!firstName && !lastName && fullNameCol && row[fullNameCol]) {
        const fullName = String(row[fullNameCol]).trim()
        if (fullName) {
          const nameParts = fullName.split(/\s+/)
          if (nameParts.length === 1) {
            // Only one word - use as first name
            firstName = nameParts[0]
          } else {
            // Last word is last name, rest is first name
            lastName = nameParts.pop()
            firstName = nameParts.join(' ')
          }
        }
      }

      return {
        serialNumber,
        skuCode: String(row[skuCol] || '').trim(),
        purchaseDate: purchaseDate || undefined,
        // Purchaser information
        purchaserFirstName: firstName,
        purchaserLastName: lastName,
        purchaserEmail: emailCol ? String(row[emailCol] || '').trim() || undefined : undefined,
        purchaserPhone: phoneCol ? String(row[phoneCol] || '').trim() || undefined : undefined,
        purchaserCountryCode: countryCodeCol ? String(row[countryCodeCol] || '').trim() || undefined : undefined,
        purchaserCountry: countryCol ? String(row[countryCol] || '').trim() || undefined : undefined,
        // Build full address from Address Line + City
        purchaserAddress: (() => {
          const addressLine = addressCol ? String(row[addressCol] || '').trim() : ''
          const city = cityCol ? String(row[cityCol] || '').trim() : ''
          const fullAddress = [addressLine, city].filter(Boolean).join(', ')
          return fullAddress || undefined
        })()
      }
    }).filter(d => d.serialNumber && d.skuCode) // Filter out rows without serial or SKU

    this.$message.success(`Loaded ${this.excelData.length} records from Excel`)
  }

  private async validateSkusAndProceed() {
    if (this.excelData.length === 0) {
      this.$message.warning('No devices to validate')
      return
    }

    this.bulkImportStep = 1
    this.bulkValidating = true

    try {
      const { data } = await bulkRegisterWithSku(this.excelData, true)

      if (data.requiresSkuCreation) {
        // Count devices per missing SKU
        const skuCounts: Record<string, number> = {}
        this.excelData.forEach(d => {
          if (data.data.missingSkus?.includes(d.skuCode)) {
            skuCounts[d.skuCode] = (skuCounts[d.skuCode] || 0) + 1
          }
        })

        this.missingSkus = (data.data.missingSkus || []).map((sku: string) => ({
          skuCode: sku,
          count: skuCounts[sku] || 0,
          created: false
        }))
        this.validatedSkuCount = data.data.existingSkus?.length || 0
        this.uniqueDeviceCount = new Set(this.excelData.map(d => d.serialNumber)).size
      } else {
        // All SKUs validated
        this.missingSkus = []
        this.validatedSkuCount = [...new Set(this.excelData.map(d => d.skuCode))].length
        this.uniqueDeviceCount = data.data.uniqueDevices || new Set(this.excelData.map(d => d.serialNumber)).size
      }
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'SKU validation failed')
      console.error(error)
      this.bulkImportStep = 0
    } finally {
      this.bulkValidating = false
    }
  }

  private get allMissingSkusCreated(): boolean {
    return this.missingSkus.length > 0 && this.missingSkus.every(s => s.created)
  }

  private handleCreateMissingSku(row: { skuCode: string, count: number, created: boolean }) {
    this.currentMissingSku = row
    this.newSkuForm = {
      skuCode: row.skuCode,
      name: row.skuCode, // Default name to SKU code
      description: '',
      productType: 'V2',
      isActive: true,
      features: []
    }
    this.newSkuFeature = {
      featureId: null,
      durationType: 'years',
      durationValue: 1
    }
    this.dialogCreateSkuVisible = true
  }

  private getFeatureNameById(featureId: number): string {
    const feature = this.featuresList.find(f => f.id === featureId)
    return feature ? feature.name : `Feature ${featureId}`
  }

  private addFeatureToNewSku() {
    if (!this.newSkuFeature.featureId) {
      this.$message.warning('Please select a feature')
      return
    }
    if (this.newSkuForm.features.some(f => f.featureId === this.newSkuFeature.featureId)) {
      this.$message.warning('Feature already added')
      return
    }
    this.newSkuForm.features.push({
      featureId: this.newSkuFeature.featureId,
      durationType: this.newSkuFeature.durationType,
      durationValue: this.newSkuFeature.durationValue
    })
    this.newSkuFeature = {
      featureId: null,
      durationType: 'years',
      durationValue: 1
    }
  }

  private removeFeatureFromNewSku(index: number) {
    this.newSkuForm.features.splice(index, 1)
  }

  private async confirmCreateSku() {
    (this.$refs.skuForm as ElForm).validate(async(valid: boolean) => {
      if (!valid) return

      try {
        // Create SKU
        const { data } = await createSku({
          skuCode: this.newSkuForm.skuCode,
          name: this.newSkuForm.name,
          description: this.newSkuForm.description,
          productType: this.newSkuForm.productType,
          isActive: this.newSkuForm.isActive
        })

        const skuId = data.data.id

        // Add features to SKU
        for (const feature of this.newSkuForm.features) {
          await addFeatureToSku(skuId, {
            featureId: feature.featureId,
            durationType: feature.durationType,
            durationValue: feature.durationType === 'permanent' ? null : feature.durationValue
          })
        }

        // Mark SKU as created
        if (this.currentMissingSku) {
          this.currentMissingSku.created = true
        }

        this.$message.success(`SKU "${this.newSkuForm.skuCode}" created successfully`)
        this.dialogCreateSkuVisible = false
      } catch (error: any) {
        this.$message.error(error.response?.data?.status?.message || 'Failed to create SKU')
        console.error(error)
      }
    })
  }

  private async proceedToRegistration() {
    this.bulkImportStep = 2
    this.bulkRegistering = true
    this.registeredCount = 0
    this.registrationProgress = 0

    try {
      const { data } = await bulkRegisterWithSku(this.excelData)

      this.registrationResult = {
        successCount: data.data.successCount || 0,
        failedCount: data.data.failedCount || 0,
        success: data.data.success || [],
        failed: data.data.failed || []
      }
      this.registeredCount = this.registrationResult.successCount
      this.registrationProgress = 100
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Registration failed')
      console.error(error)
      this.registrationResult = {
        successCount: 0,
        failedCount: this.uniqueDeviceCount,
        failed: [{ error: error.response?.data?.error || 'Registration failed' }]
      }
    } finally {
      this.bulkRegistering = false
    }
  }

  private finishBulkImport() {
    this.dialogBulkVisible = false
    this.getList()
  }

  // ============ Repair & Refund Methods ============

  private async confirmStartRepair() {
    if (!this.repairData.reason) {
      this.$message.warning('Please provide a reason for repair')
      return
    }

    try {
      const { data } = await startRepair(this.repairData.serialNumber, {
        reason: this.repairData.reason
      })

      if (data.success) {
        this.$message.success('Device sent for repair successfully')
        this.dialogRepairStartVisible = false
        this.getList()
      }
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Failed to send device for repair')
      console.error(error)
    }
  }

  private async confirmCompleteRepair() {
    if (this.repairCompleteData.repairDays < 0) {
      this.$message.warning('Repair days must be 0 or greater')
      return
    }

    try {
      const { data } = await completeRepair(this.repairCompleteData.serialNumber, {
        repairDays: this.repairCompleteData.repairDays,
        notes: this.repairCompleteData.notes
      })

      if (data.success) {
        const extendedCount = data.data?.extendedFeatures?.length || 0
        this.$message.success(
          `Repair completed. ${this.repairCompleteData.repairDays} days added to ${extendedCount} features.`
        )
        this.dialogRepairCompleteVisible = false
        this.getList()
      }
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Failed to complete repair')
      console.error(error)
    }
  }

  private async confirmRefund() {
    if (!this.refundData.reason) {
      this.$message.warning('Please provide a reason for refund')
      return
    }

    try {
      await this.$confirm(
        'This action is IRREVERSIBLE. All device features will be permanently reset. Continue?',
        'Confirm Refund',
        {
          confirmButtonText: 'Process Refund',
          cancelButtonText: 'Cancel',
          type: 'error'
        }
      )

      const { data } = await processRefund(this.refundData.serialNumber, {
        reason: this.refundData.reason
      })

      if (data.success) {
        this.$message.success('Device refund processed. All features have been reset.')
        this.dialogRefundVisible = false
        this.getList()
      }
    } catch (error: any) {
      if (error !== 'cancel') {
        this.$message.error(error.response?.data?.error || 'Failed to process refund')
        console.error(error)
      }
    }
  }

  private getProductTypeTag(type: string): string {
    const typeMap: { [key: string]: string } = {
      V1: 'info',
      V2: 'success'
    }
    return typeMap[type] || 'info'
  }

  private getStatusTagType(status: string): string {
    const statusMap: { [key: string]: string } = {
      ACTIVE: 'success',
      INACTIVE: 'info',
      REFUNDED: 'danger',
      REPLACED: 'warning',
      REPAIR: 'warning',
      ARCHIVED: 'info'
    }
    return statusMap[status] || 'info'
  }

  private getEventTypeTag(eventType: string): string {
    const eventMap: { [key: string]: string } = {
      CONNECTED: 'success',
      DISCONNECTED: 'warning',
      REFUNDED: 'danger',
      REPLACED: 'warning',
      STATUS_CHANGED: 'info'
    }
    return eventMap[eventType] || 'info'
  }

  private formatDate(date: Date | undefined): string {
    if (!date) return 'N/A'
    const d = new Date(date)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    const hours = d.getHours().toString().padStart(2, '0')
    const minutes = d.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }

  private isWarrantyExpired(warrantyEndDate: Date | undefined): boolean {
    if (!warrantyEndDate) return false
    return new Date(warrantyEndDate) < new Date()
  }

  // Joins first + last name with sensible spacing; falls back to whatever
  // single side is populated. Used in the Current User column tooltip and
  // sub-line so an empty last_name doesn't render as a trailing space.
  private userFullName(u: any): string {
    if (!u) return ''
    const fn = (u.firstName || '').trim()
    const ln = (u.lastName || '').trim()
    return [fn, ln].filter(Boolean).join(' ')
  }

  // ===== Pin Yönet drawer =====

  get pinDrawerTitle(): string {
    if (!this.pinDrawerDevice) return this.$t('devicesScreen.managePins') as string
    return this.$t('devicesScreen.managePinsFor', { serial: this.pinDrawerDevice.serialNumber }) as string
  }

  private async showPinManager(row: any) {
    this.pinDrawerDevice = row
    this.pinDrawerVisible = true
    this.pinDrawerLoading = true
    this.pinDrawerPins = []
    this.pinDrawerAllUserPins = []
    this.pinDrawerUserDevices = []
    this.pinDrawerAvailableOtaFiles = []
    try {
      const { data } = await getUserOtaInfo(row.currentUserId)
      const payload = data.data || {}
      this.pinDrawerUserDevices = payload.devices || []
      const allPins: PinListItem[] = (payload.overrides || []).map((o: any) => ({
        key: o.deviceSerialNumber || 'user-scope',
        deviceSerial: o.deviceSerialNumber,
        otaFileId: o.otaFileId,
        appVersion: o.otaFile?.applicationVersionName || '',
        wirelessVersion: o.otaFile?.wirelessVersionName || null,
        reason: o.reason,
        setBy: o.setBy,
        createdAt: o.createdAt
      }))
      this.pinDrawerAllUserPins = allPins
      // Drawer's "this device's pins" — at most one device-scope row plus the
      // user-scope row if the operator has both. PinList shows both because
      // the user-scope pin DOES still apply to this cane (when major matches).
      this.pinDrawerPins = allPins.filter(
        p => p.deviceSerial === row.serialNumber || p.deviceSerial === null
      )
      // Normalize active OTA files to the shape PinForm expects.
      this.pinDrawerAvailableOtaFiles = (payload.activeOtaFiles || []).map((f: any) => ({
        id: f.id,
        application_version_name: f.applicationVersionName,
        wireless_version_name: f.wirelessVersionName,
        is_mandatory: f.isMandatory
      }))
    } catch (err: any) {
      this.$message.error(this.$t('devicesScreen.loadDevicePinInfoFailed', { msg: err.message || '' }) as string)
    } finally {
      this.pinDrawerLoading = false
    }
  }

  private closePinDrawer(done: () => void) {
    this.pinDrawerVisible = false
    this.pinDrawerDevice = null
    this.pinDrawerPins = []
    this.pinDrawerAllUserPins = []
    this.pinDrawerUserDevices = []
    this.pinDrawerAvailableOtaFiles = []
    done()
  }

  private async onDevicePinSaved() {
    if (this.pinDrawerDevice) {
      await this.showPinManager(this.pinDrawerDevice)
    }
  }

  private async removePinFromDeviceDrawer(p: PinListItem) {
    if (!this.pinDrawerDevice) return
    const isUserScope = p.deviceSerial === null
    const scopeLabel = isUserScope ? this.$t('otaPin.allCanesScope') as string : p.deviceSerial
    try {
      await this.$confirm(
        this.$t('otaPin.removeConfirm', { version: p.appVersion, scope: scopeLabel }) as string,
        this.$t('otaPin.removeTitle') as string,
        {
          confirmButtonText: this.$t('otaPin.removeConfirmButton') as string,
          cancelButtonText: this.$t('table.cancel') as string
        }
      )
    } catch {
      return
    }
    this.pinRemovingKey = p.key
    try {
      await clearUserOtaOverride(
        this.pinDrawerDevice.currentUserId,
        isUserScope ? undefined : (p.deviceSerial || undefined),
        isUserScope
      )
      this.$message.success(this.$t('otaPin.removed') as string)
      await this.showPinManager(this.pinDrawerDevice)
    } catch (err: any) {
      this.$message.error(this.$t('otaPin.removeFailed', { msg: err.message || '' }) as string)
    } finally {
      this.pinRemovingKey = null
    }
  }
}
